import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { usePermissions } from "@/hooks/usePermissions";
import {
  postReferenceSubmission,
  submissionsKey,
  useOrgId,
  useReferenceSubmissions,
} from "@/app/routes/dashboard/references/api/useReferences";
import { getAiRequestErrorMessage } from "@/lib/api";
import { queue } from "@/components/Toast";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import type { Trend } from "@/types/trend.type";

/**
 * Filing a trend to References, from the plate's Save and from the detail sheet,
 * through one path.
 *
 * THE TREND ITSELF IS SAVED. The save sends the trend's id through the same
 * submission route the References composer uses, and the backend files the
 * trend as it is (its title, summary, tags and lead clip) as a "trend"
 * reference, inside the request. No example link is read or sent, so a trend
 * whose examples came back without links saves all the same.
 *
 * OPTIMISTIC, AND NARROW. Which trends have been filed this session is kept in
 * the query cache, per organization and brand, and every save writes its own id
 * there before its request goes out and takes only its own id back out if the
 * request fails. A batch fires one request per trend, so one failure never
 * un-saves the rest. The submissions list is reconciled once, when the LAST
 * save of a batch settles: the settling save still counts itself, hence > 1.
 */

/** The References view, where a saved trend's reference can be removed. */
export const REFERENCES_PATH = "/dashboard/references";

/**
 * What pressing Save says when the trend is already saved. A save cannot be
 * undone from Trends yet: the API gives back a submission, and nothing links it
 * to the reference the analysis job creates later, so there is no safe way to
 * find that reference from here (Mo7tawa/creative-os-backend#95). Until it can,
 * the button says where removal happens and takes the viewer there, rather than
 * doing nothing.
 */
export function showAlreadySaved(openReferences: () => void, many = false): void {
  queue.add(
    {
      title: many ? "All already saved to references" : "Already saved to references",
      description: "A save cannot be removed from Trends yet. Delete it from References.",
      action: { label: "Open References", onPress: openReferences },
    },
    { timeout: 6000 },
  );
}

/** What a failed save says in its toast. */
export function saveErrorMessage(error: unknown): string {
  return getAiRequestErrorMessage(error, "Please try again.");
}

const savedKey = (orgId: string | null, brandId: string | null) =>
  ["trendsSavedToReferences", orgId, brandId] as const;
const saveMutationKey = (orgId: string | null) => ["trendsSaveToReferences", orgId] as const;

function markSaved(
  qc: QueryClient,
  key: ReturnType<typeof savedKey>,
  trendId: string,
  saved: boolean,
): void {
  qc.setQueryData<string[]>(key, (previous = []) => {
    if (saved) return previous.includes(trendId) ? previous : [...previous, trendId];
    return previous.filter((id) => id !== trendId);
  });
}

/** Where a save would be filed, or null where there is nowhere to file it:
 *  no organization (the fixture harness) or no brand selected. */
export function useSaveTarget(): { orgId: string; brandId: string } | null {
  const orgId = useOrgId();
  const brandId = useWorkspaceStore((s) => s.selectedBrandId);
  // Filing a reference is Owner, Contributor or Guest (REFERENCE_ROLES); a Viewer
  // would see Saved, then a 403 and a rollback. No target, no Save control.
  const { canEdit, isGuest } = usePermissions();
  if (!canEdit && !isGuest) return null;
  return orgId !== null && brandId !== null ? { orgId, brandId } : null;
}

/** The trends saved for the selected brand. Two sources, merged:
 *  - this session's saves, held in the query cache so the optimistic write and
 *    its rollback use the same tools every other mutation here does;
 *  - the workspace's recent submissions that name the trend they came from, so
 *    a save survives a reload. A failed one does not count. The route returns
 *    the latest 20, so a save older than that reads as unsaved again until the
 *    backend can answer "which trends are saved" directly. */
export function useSavedTrendIds(): readonly string[] {
  const orgId = useOrgId();
  const brandId = useWorkspaceStore((s) => s.selectedBrandId);
  const { data } = useQuery({
    queryKey: savedKey(orgId, brandId),
    queryFn: () => [] as string[],
    initialData: [] as string[],
    staleTime: Infinity,
    enabled: false,
  });
  const { submissions } = useReferenceSubmissions({ enabled: brandId !== null });
  return useMemo(() => {
    const fromServer = submissions
      .filter((s) => s.trendId && s.status !== "failed" && s.brandId === brandId)
      .map((s) => s.trendId as string);
    if (fromServer.length === 0) return data;
    return [...new Set([...data, ...fromServer])];
  }, [data, submissions, brandId]);
}

export function useSaveTrendToReferences() {
  const qc = useQueryClient();
  const orgId = useOrgId();
  const brandId = useWorkspaceStore((s) => s.selectedBrandId);

  return useMutation({
    mutationKey: saveMutationKey(orgId),
    mutationFn: async (trend: Trend) => {
      if (orgId === null || brandId === null) throw new Error("Pick a brand before saving to references.");
      await postReferenceSubmission(orgId, { input: "", brandId, trendId: trend.id });
      return trend.id;
    },
    /* The keys are captured when the save STARTS and carried in the context:
       a brand switched while the request is in flight re-renders this hook
       with the new brand, and a rollback read from the closure would unmark
       the trend under the new brand's key and leave the old one saved. */
    onMutate: (trend) => {
      const key = savedKey(orgId, brandId);
      markSaved(qc, key, trend.id, true);
      return { key, orgId };
    },
    onError: (_error, trend, context) => {
      if (context) markSaved(qc, context.key, trend.id, false);
    },
    onSettled: (_data, _error, _trend, context) => {
      const org = context ? context.orgId : orgId;
      if (qc.isMutating({ mutationKey: saveMutationKey(org) }) > 1) return;
      void qc.invalidateQueries({ queryKey: submissionsKey(org) });
      /* The reference exists once the request answers, never via a submission
         seen in flight, so the wall is refreshed here rather than by the
         submissions poll. */
      void qc.invalidateQueries({ queryKey: ["references", org] });
    },
  });
}
